import React from "react";
import { styled } from "@linaria/react";
import cn from "classnames";
import ConfidenceIndicator from "../../Indicator/Confidence";
import formatDate from "../../i18n/formatDate";
import { color, fonts } from "../..";
import { colors } from "../../Theme";
import classNames from "classnames";
import t from "../../i18n";

export interface Action<TooltipAction extends string> {
  text: string;
  action: TooltipAction;
  intent: "confirm" | "danger";
  show: boolean;
  fullWidth?: boolean;
}

interface P<TooltipAction extends string> {
  title: string | null;
  date: number;
  name: string;
  value: string | null;
  otherValue?: string;
  valueExplanation: string;
  description?: string | null;
  actionTitle?: string[];
  actions?: Action<TooltipAction>[];
  selectedCount: number;
  multiSelectionString?: string;
  confidence?: number | null;
  flags?: { name: string; description: string }[];
  tagAction?: JSX.Element | null;
  timeZone: string;
  isInvalid?: boolean;
}

const Wrapper = styled.div`
  min-width: 170px;
  max-width: 350px;
  font: ${fonts.body2};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  justify-content: space-between;
`;

export const Divider = styled.div`
  border-top: 1px solid ${color.titanium200};
  margin-bottom: 10px;
  margin-top: 14px;
  width: 100%;
`;

const DateDiv = styled.p`
  font: ${fonts.body2};
  font-weight: normal;
`;

const MultipleSelectiontext = styled.p`
  min-width: 170px;
  max-width: 320px;
  font: ${fonts.body2SemiBold};
`;

const ValueTitle = styled.h2`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font: ${fonts.h4};
  padding-bottom: 8px;
`;

const SelectedValue = styled.p`
  font: ${fonts.body1};
  color: ${color.carbon800};
`;

const StyledConfidenceIndicator = styled(ConfidenceIndicator)`
  margin-left: 8px;
`;

export const ActionWrapper = styled.div`
  .separator {
    border-top: 1px solid #e6e6e6;
    padding-top: 6px;
  }
`;

export const ActionsTitle = styled.p`
  font: ${fonts.body2};
  color: ${color.carbon700};
  margin-top: 2px;
`;

export const ActionContainer = styled.div`
  display: flex;
  font: ${fonts.body2};
  gap: 8px;
`;

const Tag = styled.div`
  margin-top: 8px;
`;

export const Action = styled.a`
  text-decoration-line: underline;
  cursor: pointer;
  font: ${fonts.body2};
  :hover {
    &.confirm {
      color: ${color.darkNitrogen500};
    }
    &.danger {
      color: ${color.error};
    }
  }
  &.fullWidth {
    border-top: 1px solid ${colors.titanium200};
    flex-basis: 100%;
    text-align: center;
    padding-top: 8px;
  }
`;

const Description = styled.p`
  font: ${fonts.body2SemiBold};
  white-space: pre-wrap;
  width: 100%;
  margin-top: 8px;
  &.invalidColor {
    color: ${color.raw.oxygen400};
  }
`;

const ChartTooltip = <TooltipAction extends string>({
  date,
  selectedCount,
  name,
  value,
  description,
  actionTitle,
  actions = [],
  multiSelectionString,
  confidence,
  title,
  valueExplanation,
  flags,
  tagAction,
  otherValue,
  timeZone,
  isInvalid,
}: P<TooltipAction>) => (
  <>
    {selectedCount < 2 && (
      <Wrapper>
        <Header>
          {title}
          <DateDiv>{formatDate(date, "datetime", timeZone)}</DateDiv>
        </Header>
        {name.length > 0 && (
          <Header>
            <Divider />
            <ValueTitle>
              {name}
              {typeof confidence === "number" ? (
                <StyledConfidenceIndicator value={confidence} size="small" />
              ) : null}
            </ValueTitle>
            {value !== null && <SelectedValue>{`${valueExplanation} : ${value}`}</SelectedValue>}
          </Header>
        )}
        {otherValue || description || (flags && flags.length > 0) ? (
          <Header>
            <Divider />
            {otherValue && <SelectedValue>{otherValue}</SelectedValue>}
            {description && (
              <Description className={classNames({ invalidColor: isInvalid })}>
                {description}
              </Description>
            )}
            {flags && flags.length > 0 ? (
              <ul>
                {flags.map(flag => (
                  <li key={flag.name}>{t("highcharts.tooltip.flag.description", flag)}</li>
                ))}
              </ul>
            ) : null}
          </Header>
        ) : null}
      </Wrapper>
    )}
    {selectedCount > 1 && <MultipleSelectiontext>{multiSelectionString}</MultipleSelectiontext>}
    {actions.length && selectedCount ? (
      <>
        {actionTitle && <ActionsTitle>{actionTitle[0]}</ActionsTitle>}
        <ActionContainer>
          {actions
            .filter(a => a.show)
            .map(a => (
              <Action
                key={a.action}
                className={cn({ [a.intent]: true, fullWidth: a.fullWidth })}
                data-action={a.action}
              >
                {a.text}
              </Action>
            ))}
        </ActionContainer>
      </>
    ) : null}
    {tagAction && <Tag>{tagAction}</Tag>}
  </>
);

export default React.memo(ChartTooltip) as typeof ChartTooltip;
